import { useState } from "react";
import { Badge, Banner, BlockStack, Button, Card, DataTable, InlineStack, Text } from "@shopify/polaris";
import { api, ApiRequestError } from "../api";
import posthog from "../posthog";
import type { RatePreviewResponse } from "../types";

// "Is this thing working?" — answerable without a paid order.
//
// Before this, a merchant who connected EasyPost had no way to find out whether
// any carrier would quote for their address until their first real order came
// in and failed. An App Store reviewer, who has no orders at all, had no way to
// see the app do anything, which is most of why the submission was rejected.
//
// It rate-shops a 1 lb parcel from the shop's address to a sample US address
// and buys nothing.
export default function RatePreviewCard(): JSX.Element {
  const [result, setResult] = useState<RatePreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  async function run(): Promise<void> {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.post<RatePreviewResponse>("/rate-preview", {}));
      posthog.capture("test_rate_shop_requested");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not reach EasyPost");
    } finally {
      setRunning(false);
    }
  }

  const rows =
    result?.rates.map((rate) => [
      rate.cheapest ? (
        <InlineStack gap="200" blockAlign="center" key={`${rate.carrier}-${rate.service}`}>
          <Text as="span" fontWeight="semibold">
            {rate.carrier}
          </Text>
          <Badge tone="success">Would buy</Badge>
        </InlineStack>
      ) : (
        rate.carrier
      ),
      rate.service,
      `$${rate.rate}`,
      // A carrier that quotes no transit time has made no promise, which is a
      // real answer and not missing data — the rate filter treats it the same way.
      rate.delivery_days === null ? "Not quoted" : `${rate.delivery_days} day${rate.delivery_days === 1 ? "" : "s"}`,
    ]) ?? [];

  return (
    <Card>
      <BlockStack gap="400">
        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">
            Test rate shop
          </Text>
          <Text as="p" tone="subdued">
            Prices a 1 lb parcel from your store address to a sample address in Los Angeles and shows what
            ShipPromise would buy. Nothing is purchased and you are not charged.
          </Text>
        </BlockStack>

        {error ? <Banner tone="critical">{error}</Banner> : null}

        {result ? (
          <BlockStack gap="300">
            <Text as="p" tone="subdued" variant="bodySm">
              Rated from {[result.origin.city, result.origin.state, result.origin.country].filter(Boolean).join(", ")}
              {" · "}
              {result.parcel.length}×{result.parcel.width}×{result.parcel.height} in, {result.parcel.weight_oz} oz
            </Text>

            {/* Real orders ship from the fulfillment order's assigned location, not
                from the store address. Saying so here keeps a merchant with two
                locations from reading this quote as a per-order price. */}
            <Text as="p" tone="subdued" variant="bodySm">
              Real orders are rated from the location assigned to each fulfillment, so a shop with more than one
              location may see different prices per order.
            </Text>

            {result.carrier_accounts !== null ? (
              <Text as="p" tone="subdued" variant="bodySm">
                Carriers on your EasyPost account:{" "}
                {result.carrier_accounts.length > 0 ? result.carrier_accounts.join(", ") : "none"}
              </Text>
            ) : null}

            {result.failure ? (
              <Banner tone="critical" title={result.failure.summary}>
                <BlockStack gap="200">
                  {result.failure.fix ? <Text as="p">{result.failure.fix}</Text> : null}
                  {result.failure.details.length > 0 ? (
                    <Text as="p" tone="subdued" variant="bodySm" breakWord>
                      {result.failure.details.join(" · ")}
                    </Text>
                  ) : null}
                </BlockStack>
              </Banner>
            ) : (
              <DataTable
                columnContentTypes={["text", "text", "numeric", "text"]}
                headings={["Carrier", "Service", "Price", "Transit"]}
                rows={rows}
              />
            )}
          </BlockStack>
        ) : null}

        <div>
          <Button onClick={() => void run()} loading={running}>
            Run test rate shop
          </Button>
        </div>
      </BlockStack>
    </Card>
  );
}
